import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, Check, Copy, Mail, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { Modal } from '@/components/Modal';
import { StatusPill } from '@/components/StatusPill';
import { LoadErrorState } from '@/components/ListStates';
import { SettingsCard, SettingsPage, SettingsSection, Switch } from '@/components/settings/SettingsPage';
import {
  emailInboxesService,
  EmailInboxAddress,
  CreateEmailInboxPayload,
} from '@/services/email-inboxes.service';
import { botFlowsService, BotFlow } from '@/services/bot-flows.service';

function relativeTime(ts?: string) {
  if (!ts) return null;
  try {
    return formatDistanceToNow(new Date(ts), { addSuffix: true });
  } catch {
    return ts;
  }
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-soft text-2xs font-bold text-primary">{n}</span>
      <div className="min-w-0 flex-1 text-sm leading-relaxed text-text-secondary">{children}</div>
    </li>
  );
}

// ─── Add Inbox Modal ──────────────────────────────────────────────────────────

interface AddModalProps {
  open: boolean;
  botFlows: BotFlow[];
  onClose: () => void;
  onCreated: (inbox: EmailInboxAddress) => void;
}

function AddInboxModal({ open, botFlows, onClose, onCreated }: AddModalProps) {
  const [emailAddress, setEmailAddress] = useState('');
  const [label, setLabel] = useState('');
  const [flowUid, setFlowUid] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setEmailAddress('');
    setLabel('');
    setFlowUid('');
    setError('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailAddress.trim() || !emailAddress.includes('@')) {
      setError('Enter a valid email address');
      return;
    }
    setSaving(true);
    try {
      const payload: CreateEmailInboxPayload = {
        emailAddress: emailAddress.trim(),
        label: label.trim() || undefined,
        defaultBotFlowUid: flowUid || undefined,
      };
      const created = await emailInboxesService.create(payload);
      toast.success('Inbox registered');
      onCreated(created);
      handleClose();
    } catch {
      // Error toast shown by api interceptor
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Add email inbox">
      <form onSubmit={handleSubmit}>
        <FormField label="Email address" required error={error || undefined}>
          <Input
            type="email"
            placeholder="sales@yourcompany.com"
            value={emailAddress}
            onChange={(e) => {
              setEmailAddress(e.target.value);
              setError('');
            }}
            invalid={!!error}
            autoFocus
          />
        </FormField>

        <FormField label="Label (optional)">
          <Input placeholder="Sales, Support, General…" value={label} onChange={(e) => setLabel(e.target.value)} />
        </FormField>

        <FormField label="Default bot flow (optional)">
          <Select value={flowUid} onChange={(e) => setFlowUid(e.target.value)}>
            <option value="">— None —</option>
            {botFlows.map((f) => (
              <option key={f.flowUid} value={f.flowUid}>
                {f.name}
              </option>
            ))}
          </Select>
        </FormField>

        <div className="rounded-xl bg-surface-alt px-3.5 py-3 text-xs leading-relaxed text-text-muted">
          Then point your email provider's inbound webhook at:{' '}
          <code className="break-all rounded bg-surface px-1 py-0.5 font-mono text-[11px] text-text-secondary">
            {emailInboxesService.webhookUrl()}
          </code>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" loading={saving}>
            Add inbox
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Inbox row ────────────────────────────────────────────────────────────────

interface InboxRowProps {
  inbox: EmailInboxAddress;
  flowName?: string;
  onUpdated: (updated: EmailInboxAddress) => void;
  onDeleted: (addressUid: string) => void;
}

function InboxRow({ inbox, flowName, onUpdated, onDeleted }: InboxRowProps) {
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const toggleActive = async () => {
    setSaving(true);
    try {
      const updated = await emailInboxesService.update(inbox.addressUid, {
        isActive: !inbox.isActive,
      });
      onUpdated(updated);
    } catch {
      // interceptor handles toast
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await emailInboxesService.remove(inbox.addressUid);
      toast.success('Inbox removed');
      onDeleted(inbox.addressUid);
    } catch {
      // interceptor handles toast
    } finally {
      setSaving(false);
      setConfirmDelete(false);
    }
  };

  const received = relativeTime(inbox.lastReceivedAt);
  const meta = [received ? `Last email ${received}` : 'No email received yet', flowName && `Bot flow: ${flowName}`]
    .filter(Boolean)
    .join(' · ');

  return (
    <li className="group px-5 py-3.5 transition-colors hover:bg-surface-alt/60">
      <div className="flex items-center gap-4">
        <div
          className={clsx(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
            inbox.isActive ? 'bg-primary-soft text-primary' : 'bg-surface-alt text-text-muted',
          )}
        >
          <Mail size={17} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="min-w-0 truncate font-mono text-sm font-semibold text-text-primary" title={inbox.emailAddress}>
              {inbox.emailAddress}
            </span>
            {inbox.label && (
              <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs text-text-muted">{inbox.label}</span>
            )}
            <StatusPill status={inbox.isActive ? 'Active' : 'Paused'} variant={inbox.isActive ? 'success' : 'neutral'} />
          </div>
          <div className="mt-0.5 truncate text-xs text-text-muted" title={meta}>{meta}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Switch
            checked={inbox.isActive}
            onChange={() => void toggleActive()}
            disabled={saving}
            label={`Receive email at ${inbox.emailAddress}`}
          />
          {!confirmDelete && (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-danger-soft hover:text-danger md:opacity-0 md:transition-opacity md:group-hover:opacity-100 md:group-focus-within:opacity-100"
              aria-label={`Remove ${inbox.emailAddress}`}
              title="Remove"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {confirmDelete && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-danger/30 bg-danger-soft px-3.5 py-2.5">
          <span className="mr-auto min-w-0 text-xs text-text-secondary">
            Remove this inbox? Email sent to it will stop creating leads.
          </span>
          <Button size="sm" variant="danger" loading={saving} onClick={handleDelete}>
            Remove
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)} disabled={saving}>
            Cancel
          </Button>
        </div>
      )}
    </li>
  );
}

// ─── Forwarding instructions ──────────────────────────────────────────────────

function ForwardingCard({ provider, webhookUrl, copied, onCopy }: {
  provider: 'mailgun' | 'signed_json' | null;
  webhookUrl: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <SettingsCard>
      <div className="mb-4 text-sm font-semibold text-text-primary">
        {provider === 'signed_json' ? 'Wiring up your email relay' : 'Wiring up Mailgun'}
      </div>
      <ol className="space-y-4">
        <Step n={1}>Register the address that receives your email, using <strong className="font-semibold text-text-primary">Add inbox</strong>.</Step>
        <Step n={2}>
          Forward inbound email to this URL.
          <div className="mt-2 flex items-start gap-2 rounded-xl border border-border bg-surface-alt px-3 py-2">
            <code className="min-w-0 flex-1 break-all py-1 font-mono text-xs text-text-primary">{webhookUrl}</code>
            <Button
              size="sm"
              variant="secondary"
              iconLeft={copied ? <Check size={13} /> : <Copy size={13} />}
              onClick={onCopy}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </Step>
        {/* Only Mailgun-signed and HMAC-signed JSON posts are accepted — SendGrid / Postmark
            posts carry neither signature and were refused, despite the old instructions. */}
        <Step n={3}>
          {provider === 'signed_json' ? (
            <>Each POST must carry <code className="font-mono text-xs">X-Email-Timestamp</code> and an HMAC <code className="font-mono text-xs">X-Email-Signature</code>. Ask support for the signing details.</>
          ) : (
            <><strong className="font-semibold text-text-primary">Mailgun:</strong> Routes → Create Route → Match recipient → Forward to the URL above as an HTTP POST.</>
          )}
        </Step>
      </ol>
    </SettingsCard>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function EmailInboxesSettings() {
  const [inboxes, setInboxes] = useState<EmailInboxAddress[]>([]);
  const [botFlows, setBotFlows] = useState<BotFlow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [status, setStatus] = useState<{ ready: boolean; provider: 'mailgun' | 'signed_json' | null } | null>(null);

  const load = () => {
    setLoadError(false);
    setLoading(true);
    // Bot flows and status are optional extras — neither may take the inbox list down with it.
    Promise.all([
      emailInboxesService.list(),
      botFlowsService.list().catch(() => [] as BotFlow[]),
      emailInboxesService.status().catch(() => null),
    ])
      .then(([inboxList, flows, st]) => {
        setInboxes(inboxList);
        setBotFlows(flows);
        setStatus(st);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const webhookUrl = emailInboxesService.webhookUrl();

  const copyWebhookUrl = () => {
    navigator.clipboard
      .writeText(webhookUrl)
      .then(() => {
        setCopiedWebhook(true);
        setTimeout(() => setCopiedWebhook(false), 2000);
      })
      .catch(() => toast.error('Could not copy. Select the link and copy it by hand.'));
  };

  const flowNameOf = (flowUid?: string) => (flowUid ? botFlows.find((f) => f.flowUid === flowUid)?.name : undefined);

  return (
    <>
      <SettingsPage
        eyebrow="Channels"
        title="Email inboxes"
        description="Turn email into leads. Register the address your customers write to, forward its mail to Slightor, and every email that arrives opens a lead."
        actions={!loading && !loadError && (
          <Button iconLeft={<Plus size={14} />} onClick={() => setShowAdd(true)}>Add inbox</Button>
        )}
      >
        {/* Without a platform provider the webhook refuses every email (503), so say so first. */}
        {status && !status.ready && (
          <div className="py-6">
            <div className="flex items-start gap-2.5 rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 text-xs leading-relaxed text-text-secondary">
              <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
              <div>
                <p className="mb-0.5 font-semibold text-text-primary">Inbound email isn’t switched on yet</p>
                Addresses you add are saved, but no email will arrive until Slightor support enables inbound email for the platform.
              </div>
            </div>
          </div>
        )}

        <SettingsSection
          title="Inboxes"
          description="Email sent to an active address opens a lead. A paused address ignores incoming email."
        >
          <SettingsCard padded={false}>
            {loadError ? (
              <LoadErrorState bare what="your email inboxes" onRetry={load} />
            ) : loading ? (
              <div className="space-y-2 p-5">{[0, 1].map((i) => <div key={i} className="skeleton h-14 rounded-xl" />)}</div>
            ) : inboxes.length === 0 ? (
              <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white shadow-soft"><Mail size={20} /></div>
                <div className="text-sm font-medium text-text-primary">No email inboxes yet</div>
                <p className="max-w-sm text-xs text-text-muted">Register an address, then forward its email to Slightor. Each email that arrives creates a lead.</p>
                <Button size="sm" variant="secondary" iconLeft={<Plus size={13} />} onClick={() => setShowAdd(true)}>Add the first inbox</Button>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {inboxes.map((inbox) => (
                  <InboxRow
                    key={inbox.addressUid}
                    inbox={inbox}
                    flowName={flowNameOf(inbox.defaultBotFlowUid)}
                    onUpdated={(updated) =>
                      setInboxes((prev) =>
                        prev.map((i) => (i.addressUid === updated.addressUid ? updated : i)),
                      )
                    }
                    onDeleted={(uid) =>
                      setInboxes((prev) => prev.filter((i) => i.addressUid !== uid))
                    }
                  />
                ))}
              </ul>
            )}
          </SettingsCard>
        </SettingsSection>

        <SettingsSection
          title="Set up forwarding"
          description="Your email provider has to send incoming mail to Slightor. You only do this once."
        >
          {loading ? (
            <SettingsCard padded={false}><div className="p-5"><div className="skeleton h-32 rounded-xl" /></div></SettingsCard>
          ) : (
            <ForwardingCard
              provider={status?.provider ?? null}
              webhookUrl={webhookUrl}
              copied={copiedWebhook}
              onCopy={copyWebhookUrl}
            />
          )}
        </SettingsSection>
      </SettingsPage>

      <AddInboxModal
        open={showAdd}
        botFlows={botFlows}
        onClose={() => setShowAdd(false)}
        onCreated={(inbox) => setInboxes((prev) => [inbox, ...prev])}
      />
    </>
  );
}
